import { ErrorBoundary } from '@/components/error-boundary';
import { useAuth } from '@/context/auth';
import { useState } from 'react';
import {
  HiOutlineArrowLeftOnRectangle,
  HiOutlineBars3,
  HiOutlineBookOpen,
  HiOutlineCalendarDays,
  HiOutlineChartBarSquare,
  HiOutlineChatBubbleLeftRight,
  HiOutlineClock,
  HiOutlineDocumentText,
  HiOutlineBuildingOffice2 as HiOutlineOfficeBuilding,
  HiOutlineSquares2X2,
  HiOutlineUsers,
  HiOutlineXMark,
} from 'react-icons/hi2';
import { Link, useLocation, useLocation as useWLocation } from 'wouter';

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [location] = useLocation();
  const [, setLocation] = useWLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();

  const nav = [
    { name: 'Overview', href: '/admin', icon: HiOutlineSquares2X2 },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: HiOutlineChartBarSquare,
    },
    {
      name: 'Appointments',
      href: '/admin/appointments',
      icon: HiOutlineCalendarDays,
    },
    {
      name: 'Scheduling',
      href: '/admin/scheduling',
      icon: HiOutlineClock,
    },
    {
      name: 'Contacts',
      href: '/admin/messages',
      icon: HiOutlineChatBubbleLeftRight,
    },
    { name: 'Leads', href: '/admin/leads', icon: HiOutlineOfficeBuilding },
    { name: 'Users', href: '/admin/users', icon: HiOutlineUsers },
    { name: 'Courses', href: '/admin/courses', icon: HiOutlineBookOpen },
    { name: 'Content', href: '/admin/content', icon: HiOutlineDocumentText },
    { name: 'Proposal', href: '/admin/proposal', icon: HiOutlineDocumentText },
  ];

  const handleLogout = async () => {
    await logout();
    setLocation('/login');
  };

  return (
    <div
      className="min-h-screen flex flex-col md:flex-row"
      style={{ background: '#f7fafd' }}
    >
      {/* Mobile header */}
      <div
        className="md:hidden sticky top-0 z-50 px-5 py-4 flex justify-between items-center"
        style={{ background: '#0a2540' }}
      >
        <span className="font-bebas text-xl tracking-[0.08em] text-white">
          WCS <span className="text-[#0ea5e9]">Admin</span>
        </span>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white cursor-pointer"
        >
          {sidebarOpen ? (
            <HiOutlineXMark className="w-6 h-6" />
          ) : (
            <HiOutlineBars3 className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 flex flex-col transition-transform duration-300 md:translate-x-0 md:static md:flex ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: '#0a2540' }}
      >
        {/* Logo */}
        <div
          className="hidden md:flex items-center px-6 h-20"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <span className="font-bebas text-xl tracking-[0.08em] text-white">
            WCS <span className="text-[#0ea5e9]">Admin</span>
          </span>
        </div>

        {/* User info */}
        {user && (
          <div
            className="px-5 py-4"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#0ea5e9] flex items-center justify-center text-white font-bebas text-base">
                {user.name.charAt(0)}
              </div>
              <div>
                <div className="font-rajdhani font-bold text-[0.8rem] text-white leading-tight">
                  {user.name}
                </div>
                <div className="font-rajdhani text-[0.62rem] tracking-[0.12em] uppercase text-[rgba(255,255,255,0.45)]">
                  {user.role}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {nav.map(item => {
            const active = location === item.href;
            const Icon = item.icon;
            return (
              <Link key={item.name} href={item.href}>
                <div
                  className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl cursor-pointer transition-all"
                  style={{
                    background: active
                      ? 'rgba(14,165,233,0.18)'
                      : 'transparent',
                    color: active ? '#0ea5e9' : 'rgba(255,255,255,0.55)',
                  }}
                  onClick={() => setSidebarOpen(false)}
                  onMouseEnter={e => {
                    if (!active)
                      (e.currentTarget as HTMLElement).style.color =
                        'rgba(255,255,255,0.85)';
                  }}
                  onMouseLeave={e => {
                    if (!active)
                      (e.currentTarget as HTMLElement).style.color =
                        'rgba(255,255,255,0.55)';
                  }}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="font-rajdhani font-bold text-[0.85rem] tracking-[0.06em]">
                    {item.name}
                  </span>
                  {active && (
                    <span className="ml-auto w-1 h-1 rounded-full bg-[#0ea5e9]" />
                  )}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Footer actions */}
        <div
          className="p-3 space-y-0.5"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Link href="/">
            <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[rgba(255,255,255,0.45)] hover:text-white cursor-pointer transition-colors">
              <HiOutlineArrowLeftOnRectangle className="w-5 h-5" />
              <span className="font-rajdhani font-bold text-[0.85rem] tracking-[0.06em]">
                View Site
              </span>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[rgba(255,255,255,0.45)] hover:text-red-400 cursor-pointer transition-colors"
          >
            <HiOutlineArrowLeftOnRectangle className="w-5 h-5 rotate-180" />
            <span className="font-rajdhani font-bold text-[0.85rem] tracking-[0.06em]">
              Sign Out
            </span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-y-auto p-6 md:p-8">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
